import type { BadgeTone } from '@wapharma/design-system';
import {
  ACCESS_COVERAGE_LEVEL_FR,
  ALL_CAPABILITY_PACKS,
  ALL_DOMAIN_GRANTS,
  accessCoverageBand,
  accessCoveragePercent,
  CAPABILITY_PACK_LABEL_FR,
  grantCoveragePercent,
  grantsFromPacks,
  normalizeGrants,
  ROLE_PACKS,
  type AccessCoverageBand,
  type CapabilityPack,
  type Grant,
  normalizeCapabilityPacks,
} from '@wapharma/domain';

const ROLE_LABEL_FR: Record<string, string> = {
  SUPER_ADMIN: 'Super admin',
  CATALOG: 'Catalogue',
  BUYER: 'Acheteur',
  WAREHOUSE: 'Entrepôt',
  ACCOUNTANT: 'Comptable',
  SALES: 'Commercial',
  VIEWER: 'Lecture seule',
};

const ROLE_TONE: Record<string, BadgeTone> = {
  SUPER_ADMIN: 'danger',
  CATALOG: 'info',
  BUYER: 'warning',
  WAREHOUSE: 'success',
  ACCOUNTANT: 'info',
  SALES: 'warning',
  VIEWER: 'neutral',
};

/** End-user description: Action + scope. Limit. */
const ROLE_DESCRIPTION_FR: Record<string, string> = {
  SUPER_ADMIN: 'Ouvre toutes les capacités. Réservé aux responsables plateforme.',
  CATALOG: 'Gère produits, variantes et prix. Pas de stock ni vente.',
  BUYER: 'Prépare et envoie les bons de commande. Pas de paiement fournisseur.',
  WAREHOUSE: 'Reçoit, déplace et libère le stock. Pas d’achat ni vente.',
  ACCOUNTANT: 'Lit et écrit la compta, paie le fournisseur. Pas de vente.',
  SALES: 'Confirme et expédie les commandes clients. Pas de comptabilité.',
  VIEWER: 'Consulte listes et rapports. Aucune écriture.',
};

/** Pack detail: Action + scope. Limit when relevant. */
const CAPABILITY_PACK_DESCRIPTION_FR: Record<CapabilityPack, string> = {
  'catalog.maintain': 'Crée et modifie produits, variantes et GTIN. Pas de prix ni stock.',
  'offer.price': 'Fixe les prix catalogue. Pas de fiche produit.',
  'stock.move': 'Reçoit, transfère, ajuste et expédie le stock. Pas d’achat ni vente.',
  'buy.procure': 'Prépare et envoie les bons de commande. Pas de paiement.',
  'buy.pay': 'Enregistre les paiements fournisseur. Pas de bon de commande.',
  'sell.commit': 'Confirme, réserve et expédie les commandes clients. Pas de compta.',
  'money.read': 'Consulte journaux, balances et échéances. Pas d’écriture.',
  'money.post': 'Passe les écritures et clôtures. Pas de vente.',
  'party.admin': 'Gère comptes, rôles et rattachements. Pas d’opération métier.',
  'ops.view': 'Consulte écrans et listes. Aucune écriture.',
  'storage.read': 'Consulte les fichiers de la plateforme (médias, imports). Pas de suppression.',
  'storage.manage': 'Supprime les fichiers orphelins et purge l’espace fichiers. Réservé.',
};

export function capabilityPackLabelFr(pack: string): string {
  return CAPABILITY_PACK_LABEL_FR[pack as CapabilityPack] ?? pack;
}

export function capabilityPackDescriptionFr(pack: string): string {
  return CAPABILITY_PACK_DESCRIPTION_FR[pack as CapabilityPack] ?? 'Détail non défini pour cette capacité.';
}

export type RoleAccessCoverageSummary = {
  percent: number;
  band: AccessCoverageBand;
  levelLabelFr: string;
  heldCount: number;
  totalCount: number;
  ariaLabel: string;
};

/** List/detail Accès: how much of the system this role can touch. */
export function roleAccessCoverageSummary(
  packs: readonly string[],
  grants?: readonly string[],
): RoleAccessCoverageSummary {
  if (grants !== undefined) {
    const held = normalizeGrants(grants);
    const percent = grantCoveragePercent(held);
    const band = accessCoverageBand(percent);
    const levelLabelFr = ACCESS_COVERAGE_LEVEL_FR[band];
    const totalCount = ALL_DOMAIN_GRANTS.length;
    return {
      percent,
      band,
      levelLabelFr,
      heldCount: held.length,
      totalCount,
      ariaLabel: `${percent}% — ${levelLabelFr} (${held.length}/${totalCount} capacités)`,
    };
  }
  const held = normalizeCapabilityPacks(packs);
  const percent = accessCoveragePercent(packs);
  const band = accessCoverageBand(percent);
  const levelLabelFr = ACCESS_COVERAGE_LEVEL_FR[band];
  const totalCount = ALL_CAPABILITY_PACKS.length;
  const heldCount = held.length;
  return {
    percent,
    band,
    levelLabelFr,
    heldCount,
    totalCount,
    ariaLabel: `${percent}% — ${levelLabelFr} (${heldCount}/${totalCount} capacités)`,
  };
}

export function userRoleGrants(
  code: string,
  grants?: readonly string[],
  packs?: readonly string[],
): Grant[] {
  if (grants?.length) return normalizeGrants(grants);
  return grantsFromPacks(userRolePacks(code, packs));
}

export function userRoleLabelFr(code: string): string {
  return ROLE_LABEL_FR[code] ?? code.replace(/_/g, ' ').toLowerCase();
}

export function userRoleBadgeTone(code: string): BadgeTone {
  return ROLE_TONE[code] ?? 'neutral';
}

export function userRoleDescriptionFr(code: string, packs?: readonly string[]): string {
  if (ROLE_DESCRIPTION_FR[code]) return ROLE_DESCRIPTION_FR[code]!;
  const list = packs?.length ? packs : ROLE_PACKS[code];
  if (!list?.length) return '';
  const labels = list
    .map((p) => capabilityPackLabelFr(p))
    .filter(Boolean)
    .slice(0, 4);
  return labels.length ? `Peut : ${labels.join(', ')}.` : '';
}

export function userRolePacks(code: string, packs?: readonly string[]): CapabilityPack[] {
  if (packs?.length) {
    return packs.filter((p): p is CapabilityPack => p in CAPABILITY_PACK_LABEL_FR);
  }
  const seeded = ROLE_PACKS[code];
  return seeded ? [...seeded] : [];
}

/** Stable order for role badges and filter pills. */
export const USER_ROLE_FILTER_ORDER = [
  'SUPER_ADMIN',
  'CATALOG',
  'BUYER',
  'WAREHOUSE',
  'ACCOUNTANT',
  'SALES',
  'VIEWER',
] as const;

/** Roles assignable when super-admin creates a staff account. */
export const ADMIN_ASSIGNABLE_ROLES = [...USER_ROLE_FILTER_ORDER] as const;

/** Packs selectable when creating a custom role. */
export const CUSTOM_ROLE_PACK_OPTIONS: readonly CapabilityPack[] = ALL_CAPABILITY_PACKS;

/** Matches API create/update role SoD rejection copy (generic). */
export const SOD_CAPABILITY_MESSAGE_FR =
  'Cumul de capacités interdit : achat et paiement fournisseur, ou vente et comptabilité.';

/** Explicit SoD rule per forbidden pair — show when user selects both. */
const SOD_PAIR_RULE_FR: Record<string, string> = {
  'buy.pay|buy.procure':
    'Séparation des tâches : un même rôle ne peut pas à la fois commander chez le fournisseur et payer le fournisseur (qui commande ne paie pas).',
  'money.post|sell.commit':
    'Séparation des tâches : un même rôle ne peut pas à la fois vendre et écrire en compta (qui vend n’écrit pas la compta).',
};

function sodPairKey(a: string, b: string): string {
  return [a, b].sort().join('|');
}

export function sodPairRuleFr(a: string, b: string): string {
  return (
    SOD_PAIR_RULE_FR[sodPairKey(a, b)] ??
    `Séparation des tâches : « ${capabilityPackLabelFr(a)} » et « ${capabilityPackLabelFr(b)} » ne peuvent pas coexister sur le même rôle.`
  );
}

export function sodViolationMessagesFr(
  violations: ReadonlyArray<{ a: string; b: string }>,
): string[] {
  return violations.map((v) => sodPairRuleFr(v.a, v.b));
}

export function sodConflictHintFr(blockingPartner: string): string {
  return `Incompatible avec « ${capabilityPackLabelFr(blockingPartner)} ». ${SOD_CAPABILITY_MESSAGE_FR}`;
}
